import {
  Alert,
  ArrowLeftIcon,
  Box,
  Button,
  PlusIcon,
  Spinner,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  Tooltip,
  useToast,
} from '@razorpay/blade/components';
import api from 'api';
import { getErrorsAsString } from 'components/Kyc/utils';
import useSalaryComponentsList from 'components/RunPayroll/Popups/EditSalary/hooks/useSalaryComponentsList';
import { salaryStructureQueries } from 'components/SalaryStructures/constants';
import { useMutation, useQuery } from 'react-query';
import { AppError } from 'utils/AppError';
import SalaryRow from './SalaryRow';
import { useSalaryStructure } from './context/SalaryStructureProvider';
import {
  generatePlaceholderComponent,
  generateUpdateSalaryStructurePayload,
  parseGetSalaryStructureResponse,
  validateSalaryStructureComponents,
} from './utils';
import { useNavigation } from 'context/NavigationContext';

export const SalaryStructureTable = () => {
  const toast = useToast();
  const {
    salaryStructureComponents,
    setSalaryStructureComponents,
    addComponent,
    setSalaryStructure,
    salaryStructure,
  } = useSalaryStructure();

  const { navigate } = useNavigation();

  const {
    isFetchingSalaryComponents,
    searchSalaryComponents,
    error: errorInFetchingSalaryComponents,
    refetch: refetchSalaryComponents,
  } = useSalaryComponentsList({
    requestParams: {
      category: 'EARNINGS_CATEGORY',
      status: 'COMPONENT_STATUS_ACTIVE',
    },
  });

  const {
    isFetching: isFetchingSalaryStructure,
    error: errorInFetchingSalaryStructure,
    refetch,
  } = useQuery({
    queryKey: ['salaryStructure'],
    queryFn: () =>
      api.salaryStructures.queries.getSalaryStructure({
        input: { type: 'SALARY_STRUCTURE_TYPE_DEFAULT' },
      }),
    onSuccess(data) {
      const response = parseGetSalaryStructureResponse(data);
      setSalaryStructure({
        ...response.payrollGetSalaryStructure.data,
      });
      setSalaryStructureComponents(
        response.payrollGetSalaryStructure.data.salaryStructureComponents,
      );
    },
    onError: (e: AppError) => {},
  });

  const updateSalaryStructureMutation = useMutation({
    mutationKey: [salaryStructureQueries.updateSalaryStructure],
    mutationFn: api.salaryStructures.mutations.updateSalaryStructure,
    onSuccess: () => {
      toast.show({
        content: 'Salary structure updated successfully',
        color: 'positive',
      });
      navigate('/settings');
    },
    onError: (e: AppError) => {
      toast.show({
        content: e.message,
        color: 'negative',
      });
    },
  });

  if (isFetchingSalaryComponents || isFetchingSalaryStructure) {
    return (
      <Box display="grid" placeItems="center">
        <Spinner accessibilityLabel="Loading" size="large" />
      </Box>
    );
  }

  if (errorInFetchingSalaryStructure || errorInFetchingSalaryComponents) {
    return (
      <Alert
        isDismissible={false}
        color="negative"
        isFullWidth
        title="Error fetching salary structure details"
        description={getErrorsAsString(
          (errorInFetchingSalaryStructure as AppError) ||
            (errorInFetchingSalaryComponents as AppError),
        )}
        actions={{
          primary: {
            text: 'Retry',
            onClick: () => {
              refetchSalaryComponents();
              refetch();
            },
          },
        }}
      />
    );
  }

  if (!salaryStructureComponents.length) {
    return null;
  }

  const handleAddComponent = () => {
    const placeholderComponent = generatePlaceholderComponent();
    addComponent(placeholderComponent.id, placeholderComponent);
  };

  const handleSave = () => {
    const payload = generateUpdateSalaryStructurePayload(
      salaryStructure,
      salaryStructureComponents,
    );
    updateSalaryStructureMutation.mutate(payload);
  };

  const totalSalaryComponents = searchSalaryComponents().length;
  const isAllComponentsAdded = salaryStructureComponents.length === totalSalaryComponents;

  const { errors, combinedErrorsString } =
    validateSalaryStructureComponents(salaryStructureComponents);
  const hasValidationErrors = Object.values(errors).some((error) => error);

  return (
    <Box>
      <Table
        backgroundColor="transparent"
        data={{ nodes: salaryStructureComponents }}
        showBorderedCells
        gridTemplateColumns="1fr auto auto auto">
        {() => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>Salary Component</TableHeaderCell>
                <TableHeaderCell>%age of CTC or Amount</TableHeaderCell>
                <TableHeaderCell>Percentage or Amount?</TableHeaderCell>
                <TableHeaderCell>&nbsp;</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {salaryStructureComponents.map((item) => (
                <SalaryRow
                  key={item.id}
                  item={item}
                  searchSalaryComponents={searchSalaryComponents}
                />
              ))}
            </TableBody>
          </>
        )}
      </Table>
      <Box marginTop="spacing.6">
        {isAllComponentsAdded ? (
          <Tooltip content="You have added all the available components.">
            <Button icon={PlusIcon} variant="tertiary" isDisabled>
              Component
            </Button>
          </Tooltip>
        ) : (
          <Button
            icon={PlusIcon}
            variant="tertiary"
            onClick={handleAddComponent}
            isDisabled={salaryStructureComponents.length === totalSalaryComponents}>
            Component
          </Button>
        )}
      </Box>

      {hasValidationErrors && (
        <Box marginTop="spacing.6">
          <Alert
            isFullWidth
            isDismissible={false}
            emphasis="subtle"
            description={combinedErrorsString}
            color="negative"
          />
        </Box>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" marginTop="spacing.10">
        <Button
          isDisabled={updateSalaryStructureMutation.isLoading}
          variant="secondary"
          onClick={() => navigate('/settings')}
          icon={ArrowLeftIcon}>
          Back
        </Button>
        <Button
          isLoading={updateSalaryStructureMutation.isLoading}
          isDisabled={hasValidationErrors}
          onClick={handleSave}>
          Save & continue
        </Button>
      </Box>
    </Box>
  );
};
